function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef } from 'react';
import { setLocaleData } from '@evershop/evershop/src/lib/locale/translate';
var AppStateContext = /*#__PURE__*/React.createContext();
var AppContextDispatch = /*#__PURE__*/React.createContext();
export function AppProvider(_ref) {
  var {
    value,
    children
  } = _ref;
  var [data, setData] = React.useState(value);
  var [fetching, setFetching] = React.useState(false);
  var latestFetchRef = useRef(0);
  var updateData = useCallback(update => {
    // A direct mutation is newer than any page refresh already in flight.
    latestFetchRef.current += 1;
    setFetching(false);
    setData(update);
  }, []);
  var fetchPageData = useCallback(async url => {
    var fetchId = latestFetchRef.current + 1;
    latestFetchRef.current = fetchId;
    setFetching(true);
    try {
      var response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      var dataResponse = await response.json();
      if (latestFetchRef.current === fetchId) {
        setData(dataResponse.eContext);
      }
    } finally {
      if (latestFetchRef.current === fetchId) {
        setFetching(false);
      }
    }
  }, []);
  React.useEffect(() => {
    var refreshTimer;
    var refreshCurrentPage = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        var url = new URL(window.location.href, window.location.origin);
        url.searchParams.set('ajax', true);
        fetchPageData(url).catch(() => {
          // Keep the restored context when a background refresh is unavailable.
        });
      }, 0);
    };
    var handlePageShow = event => {
      if (event.persisted) {
        refreshCurrentPage();
      }
    };
    window.addEventListener('popstate', refreshCurrentPage);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      clearTimeout(refreshTimer);
      window.removeEventListener('popstate', refreshCurrentPage);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [fetchPageData]);
  React.useEffect(() => {
    if (data && data.language && data.translations) {
      setLocaleData({
        language: data.language,
        translations: data.translations
      });
    }
    if (typeof document !== 'undefined' && data && data.language) {
      var dir = data.dir || (data.language === 'ar' ? 'rtl' : 'ltr');
      document.documentElement.lang = data.language;
      document.documentElement.dir = dir;
    }
  }, [data]);
  var contextDispatchValue = useMemo(() => ({
    setData: updateData,
    fetchPageData
  }), [fetchPageData, updateData]);
  var contextValue = useMemo(() => _objectSpread(_objectSpread({}, data), {}, {
    fetching
  }), [data, fetching]);
  if (data && data.language && data.translations) {
    setLocaleData({
      language: data.language,
      translations: data.translations
    });
  }
  return /*#__PURE__*/React.createElement(AppContextDispatch.Provider, {
    value: contextDispatchValue
  }, /*#__PURE__*/React.createElement(AppStateContext.Provider, {
    value: contextValue
  }, children));
}
AppProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.object.isRequired
};
export var useAppState = () => React.useContext(AppStateContext);
export var useAppDispatch = () => React.useContext(AppContextDispatch);